import { AsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import NoDataSVG from '../../assets/dynamic-colors/No-DataSVG';
import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-icon.svg';
import { ReactComponent as SortAsc } from '../../assets/icons/sort-ascending.svg';
import { ReactComponent as SortDefault } from '../../assets/icons/sort-default.svg';
import { ReactComponent as SortDesc } from '../../assets/icons/sort-descending.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
// import NoDataFound from '../../assets/images/no-data-found.png';
import { ApiRequest } from '../../interface/common.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  clearSidePanelData,
  removeSidePanelData,
  setSidePanelData,
} from '../../redux/slices/TableSidePanelSlice';
import { setTextSize } from '../../redux/slices/TableTextSizeSlice';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import HighlightText from '../../shared-functions/HighlightText';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import BatteryLoader from '../battery-loader/BatteryLoader';
import Pagination from '../pagination/Pagination';
import TopHeaderButtons from '../top-header-buttons/TopHeaderButtons';

import CustomTableHeader from './components/TableHeader';
import CustomTableHeaderV2 from './components/TableHeaderV2';

type RowWithId = { id: string; [key: string]: any }; // Ensure id is either string or number
// type RowDataTypes = string | number | null;

// Define ColumnHeaderProps to be generic and flexible for any data row structure
type ColumnHeaderProperties<T> = {
  columnWidth?: number;
  label: string;
  position?: string;
  customLabel?: JSX.Element;
  accessorKey: keyof T; // Tied to the keys of the data row (T)
  customData?: (currentRow: T, index: number) => JSX.Element | null; // Custom rendering function for the data
  sortAllow?: boolean;
  queryParamsKey?: string; // This is used to push a value to search query params
  queryParamsValue?: string; // If not passed, query params will take row id by default
  sidePanelComponent?: JSX.Element;
  isSidePanelDisabled?: (currentRow: T) => boolean;
  hideWhenSidePanelOpen?: boolean; // If true, this row will be hidden when side panel is open
  handleClickExpandView?: (currentRow: any, newTab: boolean) => void; // Function to expand side panel view in full screen
  disableNewTab?: boolean; // If true, new tab option will be hidden
  allowColumnResize?: boolean; // by default it will be undefined
  headingDetails?: boolean; // show for the Heading used explain
  headingDescription?: JSX.Element;
  sticky?: 'left' | 'right';
};

interface CustomTableProperties<T extends RowWithId> {
  dataKey?: string;
  header: ColumnHeaderProperties<T>[]; // Header configuration for the table
  data: T[]; // Array of data representing rows in the table
  footerLeft?: JSX.Element;
  leftTopContent?: JSX.Element;
  rightTopContent?: JSX.Element;
  height?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  totalResults?: number;
  filter?: any;
  serialNo?: boolean;
  stickySerialNo?: boolean; // Freezes the checkbox and serial number columns to the start edge
  created?: boolean;
  lastUpdated?: boolean;
  loading?: boolean;
  disableSearch?: boolean;
  sidePanelTopLeftContent?: JSX.Element;
  actionButtonList?: {
    color: String;
    function: (currentData: string, event?: React.MouseEvent) => void;
    icon: JSX.Element;
    allow: boolean;
  }[];
  actionDropDownList?: {
    function: (value: String) => void;
    content: JSX.Element;
    allow: boolean;
  }[];
  actionButton?: boolean;
  actionDropdown?: boolean;
  filterFunction?: () => void;
  filterClearFunction?: () => void;
  pageLimitChange?: (value: ApiRequest) => void;
  sizeChangeButtonRequired?: boolean;
  filterAllow?: boolean;
  disablePagination?: boolean;
  disableHeader?: boolean;
  filters?: CommonFilterProperties[];
  filtersToDispatch?: (payload: FilterUpdateProperties) => PayloadAction<FilterUpdateProperties>;
  filterListLoading?: boolean;
  filterListToDispatch?: AsyncThunk<any, any, any>;
  tableHeaderVersion?: 'v1' | 'v2';
  rowClickEvent?: (id: string, row: any) => void;
  setHeightAutoToTr?: boolean;
  rowClickParamsKey?: string;
  rowClickSidePanel?: JSX.Element;
  refreshRequired?: boolean;
  handleRefresh?: () => void;
  hoverLabel?: string;
  handleSortChange?: (change: any) => void;
  sortData?: {
    sortBy: 'asc' | 'desc' | null;
    sortField: string | null;
  };
  isExportRequired?: boolean;
  exportDataPdfOrCSV?: (type: 'pdf' | 'csv') => void;
  exportData?: any[];
  exportLoader?: boolean;
  version?: string;
  refershCurrentPages?: () => void;
  searchHoverWidth?: string;
  onClearFilterChange?: () => void;
  activeSearchFilter?: boolean;
  hidedColumnNames?: string[];
  onHiddenColumnsChange?: (hiddenColumns: string[]) => void;
  onColumnOrderChange?: (columnOrder: string[]) => void;
  frozenColumn?: string | null;
  onFrozenColumnChange?: (columnKey: string | null) => void;
  checkbox?: boolean;
  selectedRows?: string[];
  onSelectedRowsChange?: (selected: string[]) => void;
  selectKey?: keyof T;
  expandTableOpen?: boolean;
  expandTableOpenKey?: string;
  sidePanelEnabled?: boolean;
  onSidePanelClose?: () => void;
  sidePanelManualContent?: JSX.Element;
  isRowSelectable?: (row: T) => boolean;
  explore?: boolean;
  requiredRefreshToggle?: boolean;
  refreshFunction?: () => any;
  refreshInterval?: number;
  autoRefreshEnabled?: boolean;
  disableAutoRefreshToggle?: boolean;
  refreshPosition?: 'left' | 'right';
  isAutoRefreshActive?: boolean;
  onAutoRefreshChange?: (isActive: boolean) => void;
}

/**
 * Action column label names across all supported languages (en, ar, fr, sp).
 */
const ACTION_TERMS = new Set([
  'action',
  'actions',
  'إجراء',
  'إجراءات',
  'الإجراء',
  'الإجراءات',
  'actes',
  'acción',
  'acciones',
]);

/**
 * Known non-action labels that might use 'action' as an accessorKey (e.g. log message types).
 */
const NON_ACTION_LABELS = new Set(['message type', 'action type', 'نوع الرسالة', 'نوع الإجراء']);

/**
 * Fixed width (in px) of the row selection checkbox column.
 */
const CHECKBOX_COLUMN_WIDTH = 50;

/**
 * Builds the flex shorthand for a table cell. Sticky cells must not grow beyond their
 * configured width, otherwise the computed sticky offsets would no longer line up.
 * @param {number} width - The column width in pixels
 * @param {boolean} [isSticky] - Whether the column is frozen
 * @returns {string} The flex CSS shorthand value
 */
const getCellFlex = (width: number, isSticky?: boolean): string =>
  isSticky ? `0 0 ${width}px` : `${width} 0 auto`;

/**
 * Checks whether a given table column header configuration represents an action column.
 * @param {ColumnHeaderProperties<any>} [col] - The column header configuration to check
 * @returns {boolean} True if the column is an action column, false otherwise
 */
const isActionColumn = (col?: ColumnHeaderProperties<any>): boolean => {
  if (!col) return false;
  const key = col.accessorKey ? String(col.accessorKey).trim().toLowerCase() : '';
  const label = typeof col.label === 'string' ? col.label.trim().toLowerCase() : '';

  if (ACTION_TERMS.has(label)) {
    return true;
  }

  if (key === 'action' || key === 'actions') {
    if (NON_ACTION_LABELS.has(label)) {
      return false;
    }
    return true;
  }

  return false;
};

/**
 * CustomTable component
 * @param {any} properties - The properties
 * @returns {React.JSX.Element} The rendered custom table component
 */
function CustomTable<T extends RowWithId>(properties: CustomTableProperties<T>): React.JSX.Element {
  const {
    dataKey,
    header,
    data,
    footerLeft,
    loading,
    height = 0,
    page = 0,
    limit = 0,
    totalPages = 0,
    totalResults = 0,
    pageLimitChange = () => { },
    leftTopContent,
    rightTopContent,
    actionButton,
    sizeChangeButtonRequired,
    filterAllow,
    filterFunction = () => { },
    filterClearFunction = () => { },
    disablePagination,
    disableHeader,
    disableSearch,
    filters,
    filtersToDispatch,
    filterListLoading,
    filterListToDispatch,
    tableHeaderVersion,
    rowClickEvent,
    rowClickParamsKey,
    rowClickSidePanel,
    setHeightAutoToTr,
    refreshRequired,
    handleRefresh,
    hoverLabel,
    serialNo,
    stickySerialNo,
    created,
    lastUpdated,
    handleSortChange = () => { },
    sortData,
    exportDataPdfOrCSV,
    exportData,
    exportLoader,
    isExportRequired,
    version,
    refershCurrentPages,
    searchHoverWidth,
    onClearFilterChange,
    onHiddenColumnsChange,
    onColumnOrderChange,
    frozenColumn: controlledFrozenColumn,
    onFrozenColumnChange,
    activeSearchFilter,
    hidedColumnNames = [],
    checkbox,
    selectedRows = [],
    onSelectedRowsChange = () => { },
    sidePanelTopLeftContent,
    selectKey = '_id',
    expandTableOpen,
    expandTableOpenKey,
    sidePanelEnabled,
    onSidePanelClose,
    sidePanelManualContent,
    isRowSelectable,
    explore = true,
    requiredRefreshToggle,
    refreshFunction,
    refreshInterval,
    autoRefreshEnabled,
    disableAutoRefreshToggle,
    refreshPosition = 'right',
    isAutoRefreshActive,
    onAutoRefreshChange,
  } = properties;

  console.log();

  const { textSize } = useAppSelector((state) => state.tableTextSize);
  const fullScreenKey = useAppSelector((state) => state.layout.fullScreenKey);

  const { t, i18n } = useTranslation('sharedComponent');
  const dispatch = useAppDispatch();

  const containerReference = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const { activeRow } = useAppSelector((state) => state.tableSidePanel);
  const [selectedRowId, setSelectedRowId] = useState('');
  const [tableSize, setTableSize] = useState<'small' | 'medium' | 'large'>(textSize);
  const [textToHighlight, setTextToHighlight] = useState<string | null>(null);
  const [widthOfContainers, setWidthOfContainers] = useState({
    left: 100, // percentage
    right: 0,
    applyTransitionEffect: true,
  });

  /**
   * handleSelectAll
   */
  const handleSelectAll = () => {
    const pageIds = data
      .filter((row) => (isRowSelectable ? isRowSelectable(row) : true))
      .map((row) => String(row[selectKey]));

    const isAllSelected = pageIds.every((id) => selectedRows.includes(id));

    if (isAllSelected) {
      onSelectedRowsChange(selectedRows.filter((id) => !pageIds.includes(id)));
    } else {
      onSelectedRowsChange([
        ...selectedRows,
        ...pageIds.filter((id) => !selectedRows.includes(id)),
      ]);
    }
  };

  /**
   * handleCheckRow
   * @param {any} row row
   */
  const handleCheckRow = (row: T) => {
    const value = String(row[selectKey]);

    if (selectedRows.includes(value)) {
      onSelectedRowsChange(selectedRows.filter((item) => item !== value));
    } else {
      onSelectedRowsChange([...selectedRows, value]);
    }
  };

  // Code to handle params related functions
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [sidePanelDetails, setSidePanelDetails] = useState<{
    jsxElement: JSX.Element;
    enableExpandFullScreen: boolean;
    disableNewTab?: boolean;
  }>({
    jsxElement: <div />,
    enableExpandFullScreen: false,
    disableNewTab: false,
  });

  // -------------- View & Hide column functionality ------------------ //
  const [hidedColumn, setHidedColumn] = useState<string[]>(hidedColumnNames);
  // Added tooltipState for the heading content
  const [tooltipState, setTooltipState] = useState<{
    isOpen: boolean;
    style: React.CSSProperties;
    columnIndex: number | null;
  }>({
    isOpen: false,
    style: {},
    columnIndex: null,
  });

  const wrapperReferences = useRef<(HTMLSpanElement | null)[]>([]);

  const initialWidths = header.reduce(
    (accumulator, col) => {
      return {
        ...accumulator,
        [col.accessorKey.toString()]: col.columnWidth || 140,
      };
    },
    {} as { [key: string]: number },
  );

  if (serialNo) {
    initialWidths.serialNo = 50;
  }
  if (created) {
    initialWidths.createdAt = 150;
  }
  if (lastUpdated) {
    initialWidths.updatedAt = 150;
  }
  const [columnWidths, setColumnWidths] = useState(initialWidths);

  /**
   * Function to handle table size change
   */
  const handleChangeTableSize = () => {
    if (tableSize === 'small') {
      setTableSize('medium');
      dispatch(setTextSize('medium'));
    } else if (tableSize === 'medium') {
      setTableSize('large');
      dispatch(setTextSize('large'));
    } else if (tableSize === 'large') {
      setTableSize('small');
      dispatch(setTextSize('small'));
    }
  };

  /**
   * Change the page function
   * @param {any} changeDate change date request
   */
  const changePage = (changeDate: ApiRequest) => {
    pageLimitChange(changeDate);
  };

  /**
   * Change sort function
   * @param {any} changeLimit change limit
   */
  const handleSortFieldTable = (changeLimit: string) => {
    if (!loading) {
      handleSortChange({
        ...sortData,
        sortField: changeLimit,
        sortBy: sortData?.sortField === changeLimit && sortData.sortBy === 'asc' ? 'desc' : 'asc',
      });
    }
  };

  // Create a style object with CSS variables for each header
  const tableStyle: React.CSSProperties = header.reduce((styleObject, eachHeader) => {
    const width = eachHeader.columnWidth || 140;
    const key = eachHeader.accessorKey.toString();
    return {
      ...styleObject,
      [`--column-${key}-size`]: width,
    };
  }, {});

  /**
   * Function to handle table click event
   * @param {any} row row data
   */
  const handleRowClickEvent = (row: any) => {
    if (!rowClickEvent || !rowClickParamsKey) return;
    const parameterValue = row[rowClickParamsKey] || row.id; // <-- dynamic param
    rowClickEvent(parameterValue, row);
  };

  /**
   * Function to handle mouse move event
   */
  const handleMouseDownFunction = () => {
    isDragging.current = true;
  };

  /**
   * handleMouseDown
   * @param {any} event mouse event
   * @param {any} key column key
   */
  const handleMouseDown = (event: React.MouseEvent, key: string) => {
    const startX = event.clientX;
    // eslint-disable-next-line no-template-curly-in-string
    const startWidth = Number(columnWidths[key]);

    /**
     * handleMouseMove
     * @param {any} moveEvent mouse event
     */
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const columnMovement = moveEvent.clientX - startX;
      const newWidth = Math.min(Math.max(startWidth + columnMovement, 40), 1000); // Ensure a minimum width of 40px and maximum width of 1000px

      setColumnWidths({
        ...columnWidths,
        [key]: newWidth,
      });
    };

    /**
     * handleMouseUp
     */
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  /**
   * Add or update a specific param, while keeping others
   * @param {any} key key
   * @param {any} value value
   */
  const updateQueryParameter = (key: string, value: string) => {
    const parameters = new URLSearchParams(searchParameters.toString());
    const sidePanelKeys = [
      'accountCustomId',
      'deviceId',
      'deviceHubId',
      'connectorDeviceId',
      'stationId',
      'facilityId',
      'userCustomId',
      'userId',
      'utilityId',
      'transactionObjectId',
      'roleId',
      'sessionId',
      'cdrObjectId',
      'paymentDetailsId',
      'personalChargerId',
      'action',
    ];

    if (sidePanelKeys.includes(key)) {
      sidePanelKeys.forEach((k) => {
        if (k !== key) {
          parameters.delete(k);
        }
      });
      parameters.delete('action');
    }

    parameters.set(key, value);
    setSearchParameters(parameters);
  };

  /**
   * Function to handle side panel expand view
   * @param {any} newTab - The newTab
   */
  const handleSidePanelExpand = (newTab: boolean) => {
    const parameters = new URLSearchParams(searchParameters);

    // Convert URLSearchParams to a plain object
    const parametersObject: Record<string, string> = {};
    parameters.forEach((value, key) => {
      parametersObject[key] = value;
    });
    const urlKey = Object.keys(parametersObject)[0];

    const activeSidePanelHeader = header.find(
      (eachHeader) =>
        eachHeader.queryParamsKey === urlKey || eachHeader.queryParamsValue === urlKey,
    );

    if (activeSidePanelHeader?.handleClickExpandView) {
      const value = parametersObject[urlKey];

      // Resolve correct row using id OR custom value selector
      const item =
        data.find((object) => object.id === value) ||
        data.find((object) => object[activeSidePanelHeader.queryParamsValue || 'id'] === value);

      activeSidePanelHeader.handleClickExpandView(item || null, newTab);
    }
  };

  /**
   * Handling row click
   * @param {any} row row data
   * @param {any} col column data
   * @param {any} event mouse event
   */
  const handleRowClick = (row: any, col: any, event?: React.MouseEvent) => {
    if (col?.queryParamsKey && col?.sidePanelComponent) {
      if (col.isSidePanelDisabled && col.isSidePanelDisabled(row)) return;

      event?.stopPropagation();
      const queryValue = col.queryParamsValue ? row[col.queryParamsValue] : undefined;
      updateQueryParameter(col.queryParamsKey.toString(), queryValue || row.id);
      dispatch(
        setSidePanelData({
          key: dataKey,
          value: row.id,
        }),
      );
    }
  };

  /**
   * Function to close side-panel
   */
  const handleCloseSidePanel = () => {
    if (!sidePanelEnabled) {
      setWidthOfContainers({
        left: 100,
        right: 0,
        applyTransitionEffect: true,
      });
    }
    const keysToRemove: string[] = ['action'];

    header.forEach((eachHeader) => {
      if (eachHeader?.queryParamsKey) {
        keysToRemove.push(eachHeader?.queryParamsKey);
      }
    });

    if (rowClickParamsKey) {
      keysToRemove.push(rowClickParamsKey);
    }

    // Remove only the specified keys from search parameters (URLSearchParams)
    setSearchParameters((previous) => {
      const updatedParameters = new URLSearchParams(previous.toString());
      keysToRemove.forEach((key) => {
        updatedParameters.delete(key);
      });
      return updatedParameters;
    });

    // Update container widths to close the right side panel with transition
    setWidthOfContainers({
      left: 100,
      right: 0,
      applyTransitionEffect: true,
    });
    // Remove side panel data if stored in Redux
    const parameters = new URLSearchParams(searchParameters);

    // Convert URLSearchParams to a plain object
    const parametersObject: Record<string, string> = {};
    parameters.forEach((value, key) => {
      parametersObject[key] = value;
    });

    const key = Object.keys(parametersObject)[0];

    dispatch(removeSidePanelData({ type: key }));
    dispatch(clearSidePanelData({ key: dataKey }));
    if (onSidePanelClose) {
      onSidePanelClose();
    }
  };

  /**
   * Added tooltipState for the heading content functions
   * @param {any} index index
   */
  const toggleTooltip = (index: number) => {
    const wrapperRefe = wrapperReferences.current[index];
    const tooltipElement = document.getElementById('dynamic-tooltip'); // add ref or id to tooltip

    if (!tooltipState.isOpen && wrapperRefe) {
      const rect = wrapperRefe.getBoundingClientRect();
      const margin = 4;

      // Measure tooltip width dynamically
      const tooltipWidth = tooltipElement ? tooltipElement.offsetWidth : 0;

      const top = rect.bottom + window.scrollY + 6;
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;

      if (left < margin) left = margin;
      if (left + tooltipWidth > window.innerWidth - margin) {
        left = window.innerWidth - tooltipWidth - margin;
      }

      setTooltipState({
        isOpen: true,
        style: {
          position: 'absolute',
          top,
          left,
          zIndex: 9999,
        },
        columnIndex: index,
      });
    } else {
      setTooltipState((previous) => ({
        ...previous,
        isOpen: false,
        columnIndex: null,
      }));
    }
  };

  useEffect(() => {
    const parameters = new URLSearchParams(searchParameters);

    // Convert URLSearchParams to a plain object
    const parametersObject: Record<string, string> = {};
    parameters.forEach((value, key) => {
      parametersObject[key] = value;
    });

    const jsxElementToShow = header.find(
      (eachHeader) =>
        eachHeader.queryParamsKey &&
        parametersObject[eachHeader.queryParamsKey] &&
        eachHeader.sidePanelComponent,
    );

    // If column click side panel was not found then, check for row click
    if (
      !jsxElementToShow &&
      rowClickSidePanel &&
      rowClickParamsKey &&
      Object.keys(parametersObject)?.includes(rowClickParamsKey)
    ) {
      setSidePanelDetails({
        jsxElement: rowClickSidePanel || <div>{t('somethingWentWrong')}</div>,
        enableExpandFullScreen: false, // Need to handle navigate full screen icon if required
        disableNewTab: false,
      }); // If required, need to show the proper error message

      setWidthOfContainers({
        left: 60,
        right: 40,
        applyTransitionEffect: true,
      });
    }

    if (parametersObject && jsxElementToShow) {
      setWidthOfContainers({
        left: 60,
        right: 40,
        applyTransitionEffect: true,
      });

      setSidePanelDetails({
        jsxElement: jsxElementToShow.sidePanelComponent || <div>{t('somethingWentWrong')}</div>,
        enableExpandFullScreen: Boolean(jsxElementToShow.handleClickExpandView),
        disableNewTab: jsxElementToShow.disableNewTab,
      }); // If required, need to show the proper error message
    }

    const hasSidePanelKey = Boolean(
      (rowClickSidePanel && rowClickParamsKey && parametersObject[rowClickParamsKey]) ||
        jsxElementToShow,
    );

    if (!hasSidePanelKey) {
      setWidthOfContainers({
        left: 100,
        right: 0,
        applyTransitionEffect: true,
      });
    }
  }, [searchParameters]);

  useEffect(() => {
    if (sidePanelEnabled) {
      setWidthOfContainers({
        left: 60,
        right: 40,
        applyTransitionEffect: true,
      });

      if (sidePanelManualContent) {
        setSidePanelDetails({
          jsxElement: sidePanelManualContent,
          enableExpandFullScreen: false,
          disableNewTab: false,
        });
      }
    } else {
      setWidthOfContainers({
        left: 100,
        right: 0,
        applyTransitionEffect: true,
      });
    }
  }, [sidePanelEnabled]);

  useEffect(() => {
    if (onHiddenColumnsChange) {
      onHiddenColumnsChange(hidedColumn);
    }
  }, [hidedColumn]);

  useEffect(() => {
    if (filters?.length) {
      const searchBy = filters.find((eachFilter) => eachFilter.searchBy);
      setTextToHighlight(searchBy?.value?.toString() || null);
    }
  }, [filters]);

  useEffect(() => {
    const activeKeyValue = activeRow.find((row) => row.key === dataKey);

    if (activeRow) setSelectedRowId(activeKeyValue?.value || '');
  }, [dataKey, activeRow, searchParameters]);

  const showCreatedColumn = Boolean(created) && !hidedColumn.includes('createdAt');
  const showLastUpdatedColumn = Boolean(lastUpdated) && !hidedColumn.includes('updatedAt');

  const defaultColumnKeys = React.useMemo(() => {
    const keys: string[] = [];
    const actionIndex = header.findIndex((column) => isActionColumn(column));
    const beforeAction = actionIndex !== -1 ? header.slice(0, actionIndex) : header;
    const actionColumns = actionIndex !== -1 ? header.slice(actionIndex) : [];

    beforeAction.forEach((column) => keys.push(column.accessorKey.toString()));
    if (created) keys.push('createdAt');
    if (lastUpdated) keys.push('updatedAt');
    actionColumns.forEach((column) => keys.push(column.accessorKey.toString()));
    return keys;
  }, [header, created, lastUpdated]);

  const [internalFrozenColumn, setInternalFrozenColumn] = useState<string | null>(() => {
    if (dataKey) {
      try {
        const saved = localStorage.getItem(`${dataKey}_frozen_column`);
        if (saved !== null) {
          return saved ? JSON.parse(saved) : null;
        }
      } catch {
        // Ignore
      }
    }
    const initialLeft = header.find((col) => col.sticky === 'left');
    return initialLeft ? initialLeft.accessorKey.toString() : null;
  });

  const effectiveFrozenColumn =
    controlledFrozenColumn !== undefined ? controlledFrozenColumn : internalFrozenColumn;

  /**
   * Handle column freeze toggle
   * @param {string | null} nextFrozen - Next frozen column key or null
   */
  const handleFrozenColumnChange = (nextFrozen: string | null) => {
    setInternalFrozenColumn(nextFrozen);
    if (dataKey) {
      try {
        localStorage.setItem(`${dataKey}_frozen_column`, JSON.stringify(nextFrozen));
      } catch {
        // Ignore
      }
    }
    if (onFrozenColumnChange) {
      onFrozenColumnChange(nextFrozen);
    }
  };

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (dataKey) {
      try {
        const saved = localStorage.getItem(`${dataKey}_column_order`);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const valid = parsed.filter((key) => defaultColumnKeys.includes(String(key)));
            const missing = defaultColumnKeys.filter((key) => !valid.includes(key));
            return [...valid, ...missing];
          }
        }
      } catch {
        // Fallback to defaultColumnKeys
      }
    }
    return defaultColumnKeys;
  });

  useEffect(() => {
    setColumnOrder((previousOrder) => {
      if (previousOrder.length === 0) return defaultColumnKeys;
      const valid = previousOrder.filter((key) => defaultColumnKeys.includes(key));
      const missing = defaultColumnKeys.filter((key) => !valid.includes(key));
      const nextOrder = [...valid, ...missing];
      if (
        nextOrder.length === previousOrder.length &&
        nextOrder.every((key, index) => key === previousOrder[index])
      ) {
        return previousOrder;
      }
      return nextOrder;
    });
  }, [defaultColumnKeys]);

  /**
   * Handle column order change from drag and drop
   * @param {string[]} newOrder - New column accessor keys order
   */
  const handleColumnOrderChange = (newOrder: string[]) => {
    setColumnOrder(newOrder);
    if (dataKey) {
      try {
        localStorage.setItem(`${dataKey}_column_order`, JSON.stringify(newOrder));
      } catch {
        // Ignore localStorage error
      }
    }
    if (onColumnOrderChange) {
      onColumnOrderChange(newOrder);
    }
  };

  const headerActionIndex = header.findIndex((column) => isActionColumn(column));
  const beforeHeaderAction = headerActionIndex !== -1 ? header.slice(0, headerActionIndex) : header;
  const headerActionCols = headerActionIndex !== -1 ? header.slice(headerActionIndex) : [];

  const rawLabelForHideColumnList = React.useMemo(() => [
    ...beforeHeaderAction.map((eachHeader) => ({
      label: eachHeader.label,
      accessorKey: eachHeader.accessorKey,
      disableHideFunc: false,
    })),
    ...(created ? [{ label: t('created'), accessorKey: 'createdAt', disableHideFunc: false }] : []),
    ...(lastUpdated
      ? [
        {
          label: t('lastUpdated'),
          accessorKey: 'updatedAt',
          disableHideFunc: false,
        },
      ]
      : []),
    ...headerActionCols.map((eachHeader) => ({
      label: eachHeader.label,
      accessorKey: eachHeader.accessorKey,
      disableHideFunc: false,
    })),
  ], [beforeHeaderAction, created, lastUpdated, headerActionCols, t]);

  const labelForHideColumnList = React.useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) return rawLabelForHideColumnList;
    const orderIndexMap = new Map<string, number>();
    columnOrder.forEach((key, index) => {
      orderIndexMap.set(key, index);
    });

    return [...rawLabelForHideColumnList].sort((a, b) => {
      const keyA = String(a.accessorKey);
      const keyB = String(b.accessorKey);
      const indexA = orderIndexMap.has(keyA) ? (orderIndexMap.get(keyA) as number) : 999;
      const indexB = orderIndexMap.has(keyB) ? (orderIndexMap.get(keyB) as number) : 999;
      return indexA - indexB;
    });
  }, [rawLabelForHideColumnList, columnOrder]);

  type UnifiedColItem =
    | { kind: 'column'; col: ColumnHeaderProperties<T>; key: string }
    | { kind: 'created'; key: 'createdAt' }
    | { kind: 'updated'; key: 'updatedAt' };

  const unifiedCols = React.useMemo(() => {
    const colMap = new Map<string, ColumnHeaderProperties<T>>();
    header.forEach((column) => colMap.set(column.accessorKey.toString(), column));

    const list: UnifiedColItem[] = [];
    const seen = new Set<string>();

    columnOrder.forEach((key) => {
      if (key === 'createdAt' && showCreatedColumn) {
        list.push({ kind: 'created', key: 'createdAt' });
        seen.add(key);
      } else if (key === 'updatedAt' && showLastUpdatedColumn) {
        list.push({ kind: 'updated', key: 'updatedAt' });
        seen.add(key);
      } else {
        const column = colMap.get(key);
        if (column && !hidedColumn.includes(key)) {
          if (!(column.hideWhenSidePanelOpen && widthOfContainers.right > 0)) {
            list.push({ kind: 'column', col: column, key });
          }
          seen.add(key);
        }
      }
    });

    header.forEach((column) => {
      const key = column.accessorKey.toString();
      if (!seen.has(key) && !hidedColumn.includes(key)) {
        if (!(column.hideWhenSidePanelOpen && widthOfContainers.right > 0)) {
          list.push({ kind: 'column', col: column, key });
        }
      }
    });

    if (showCreatedColumn && !seen.has('createdAt')) {
      list.push({ kind: 'created', key: 'createdAt' });
    }
    if (showLastUpdatedColumn && !seen.has('updatedAt')) {
      list.push({ kind: 'updated', key: 'updatedAt' });
    }

    return list;
  }, [
    header,
    columnOrder,
    hidedColumn,
    showCreatedColumn,
    showLastUpdatedColumn,
    widthOfContainers.right,
  ]);

  const frozenKeys = React.useMemo(() => {
    if (!effectiveFrozenColumn) return new Set<string>();

    const orderIndex = columnOrder.indexOf(effectiveFrozenColumn);
    if (orderIndex !== -1) {
      return new Set(columnOrder.slice(0, orderIndex + 1));
    }

    const unifiedIndex = unifiedCols.findIndex((item) => item.key === effectiveFrozenColumn);
    if (unifiedIndex !== -1) {
      return new Set(unifiedCols.slice(0, unifiedIndex + 1).map((item) => item.key));
    }

    return new Set<string>([effectiveFrozenColumn]);
  }, [effectiveFrozenColumn, columnOrder, unifiedCols]);

  const isSerialSticky = Boolean(
    stickySerialNo ||
      Boolean(effectiveFrozenColumn) ||
      unifiedCols.some((item) => item.kind === 'column' && item.col.sticky === 'left'),
  );

  const stickyOffsets = React.useMemo(() => {
    const offsets: { [key: string]: { left?: number; right?: number } } = {};

    let currentLeftOffset = 0;
    if (isSerialSticky && checkbox) {
      offsets.__checkbox__ = { left: currentLeftOffset };
      currentLeftOffset += CHECKBOX_COLUMN_WIDTH;
    }
    if (isSerialSticky && serialNo) {
      offsets.serialNo = { left: currentLeftOffset };
      currentLeftOffset += Math.max(columnWidths.serialNo || 50, 40);
    }
    for (let index = 0; index < unifiedCols.length; index += 1) {
      const item = unifiedCols[index];
      const { key } = item;
      const isLeftSticky =
        effectiveFrozenColumn !== null
          ? frozenKeys.has(key)
          : item.kind === 'column' && item.col.sticky === 'left';

      if (isLeftSticky) {
        offsets[key] = { left: currentLeftOffset };
        const colWidth =
          item.kind === 'column'
            ? columnWidths[key] || item.col.columnWidth || 140
            : columnWidths[key] || 150;
        currentLeftOffset += colWidth;
      }
    }

    let currentRightOffset = 0;
    for (let index = unifiedCols.length - 1; index >= 0; index -= 1) {
      const item = unifiedCols[index];
      if (item.kind === 'column' && item.col.sticky === 'right') {
        const { key } = item;
        offsets[key] = { right: currentRightOffset };
        currentRightOffset += columnWidths[key] || item.col.columnWidth || 140;
      }
    }

    return offsets;
  }, [
    unifiedCols,
    columnWidths,
    isSerialSticky,
    checkbox,
    serialNo,
    effectiveFrozenColumn,
    frozenKeys,
  ]);

  return (
    <div
      className={`custom-table-wrap ${i18n.language === 'ar' ? 'rtl' : ''} ${fullScreenKey && expandTableOpenKey && fullScreenKey === expandTableOpenKey ? 'fullscreen-active' : ''}`}>
      {!disableHeader && !filters && tableHeaderVersion === 'v1' && (
        <TopHeaderButtons
          filterFunction={filterFunction}
          filterClearFunction={filterClearFunction}
          sizeChangeButtonRequired={sizeChangeButtonRequired}
          sizeChangeFunction={handleChangeTableSize}
          filterAllow={filterAllow}
          leftTopContent={leftTopContent}
          rightTopContent={rightTopContent}
        />
      )}
      {version === 'v2' &&
        !disableHeader &&
        ((filters && filterListToDispatch) ||
          requiredRefreshToggle ||
          refershCurrentPages ||
          isExportRequired ||
          sizeChangeButtonRequired ||
          leftTopContent ||
          rightTopContent) ? (
        <CustomTableHeaderV2
          isExportRequired={isExportRequired}
          currentFontSize={tableSize}
          sizeChangeButtonRequired={sizeChangeButtonRequired}
          sizeChangeFunction={handleChangeTableSize}
          filters={filters}
          filtersToDispatch={filtersToDispatch}
          filterListToDispatch={filterListToDispatch}
          filterListLoading={filterListLoading || false}
          disableSearch={disableSearch}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={exportData || data}
          exportLoader={exportLoader}
          labelForHideColumn={labelForHideColumnList}
          hidedColumn={hidedColumn}
          setHidedColumn={setHidedColumn}
          onColumnOrderChange={handleColumnOrderChange}
          frozenColumn={effectiveFrozenColumn}
          onFrozenColumnChange={handleFrozenColumnChange}
          leftTopContent={leftTopContent}
          rightTopContent={rightTopContent}
          refershCurrentPages={refershCurrentPages}
          searchHoverWidth={searchHoverWidth}
          onClearFilterChange={onClearFilterChange}
          activeSearchFilter={activeSearchFilter}
          expandTableOpen={expandTableOpen}
          expandTableOpenKey={expandTableOpenKey}
          requiredRefreshToggle={requiredRefreshToggle}
          refreshFunction={refreshFunction}
          refreshInterval={refreshInterval}
          autoRefreshEnabled={autoRefreshEnabled}
          disableAutoRefreshToggle={disableAutoRefreshToggle}
          refreshPosition={refreshPosition}
          isAutoRefreshActive={isAutoRefreshActive}
          onAutoRefreshChange={onAutoRefreshChange}
          filterAllow={filterAllow}
        />
      ) : null}
      {(version === 'v1' && filters && filterListToDispatch) || tableHeaderVersion === 'v2' ? ( // Table header v2 will enable header without filter operations
        <CustomTableHeader
          currentFontSize={tableSize}
          sizeChangeButtonRequired={sizeChangeButtonRequired}
          sizeChangeFunction={handleChangeTableSize}
          filters={filters}
          filtersToDispatch={filtersToDispatch}
          filterListToDispatch={filterListToDispatch}
          filterListLoading={filterListLoading || false}
          disableSearch={disableSearch}
          leftTopContent={leftTopContent}
          rightTopContent={rightTopContent}
          labelForHideColumn={labelForHideColumnList}
          hidedColumn={hidedColumn}
          setHidedColumn={setHidedColumn}
          onColumnOrderChange={handleColumnOrderChange}
          frozenColumn={effectiveFrozenColumn}
          onFrozenColumnChange={handleFrozenColumnChange}
          refreshRequired={refreshRequired}
          handleRefresh={handleRefresh}
          hoverLabel={hoverLabel}
        />
      ) : null}
      <div className="custom-table-with-side-panel" ref={containerReference}>
        <div
          className="custom-table"
          style={{
            width: `${widthOfContainers.left}%`,
            transition: `width ${widthOfContainers.applyTransitionEffect ? '0.2s' : '0.0s'} ease-in-out`,
          }}>
          <table
            className={`table-container ${tableSize}`}
            style={tableStyle}>
            <thead className="table-header-wrap">
              <tr className={`table-header-container ${!actionButton && 'no-action'}`}>
                {checkbox && (
                  <th
                    className={`table-header checkbox ${isSerialSticky ? 'sticky-column sticky-left' : ''}`}
                    style={
                      {
                        width: `${CHECKBOX_COLUMN_WIDTH}px`,
                        flex: isSerialSticky ? getCellFlex(CHECKBOX_COLUMN_WIDTH, true) : undefined,
                        '--sticky-left': isSerialSticky
                          ? `${stickyOffsets.__checkbox__?.left ?? 0}px`
                          : undefined,
                        '--sticky-right': isSerialSticky
                          ? `${stickyOffsets.__checkbox__?.left ?? 0}px`
                          : undefined,
                      } as React.CSSProperties
                    }>
                    <input
                      type="checkbox"
                      aria-label={t('selectAllRows')}
                      checked={(() => {
                        const selectableRows = data.filter((row) =>
                          isRowSelectable ? isRowSelectable(row) : true,
                        );
                        return (
                          selectableRows.length > 0 &&
                          selectableRows.every((row) =>
                            selectedRows.includes(String(row[selectKey])),
                          )
                        );
                      })()}
                      onChange={handleSelectAll}
                    />
                  </th>
                )}

                {serialNo && (
                  <th
                    className={`table-header ${isSerialSticky ? 'sticky-column sticky-left' : ''}`}
                    key="serialNo"
                    style={{
                      minWidth: `${Math.max(columnWidths.serialNo, 40)}px`,
                      width: `${columnWidths.serialNo}px`,
                      flex: getCellFlex(columnWidths.serialNo, isSerialSticky),
                      ...({
                        '--sticky-left': isSerialSticky
                          ? `${stickyOffsets.serialNo?.left ?? 0}px`
                          : undefined,
                        '--sticky-right': isSerialSticky
                          ? `${stickyOffsets.serialNo?.left ?? 0}px`
                          : undefined,
                      } as React.CSSProperties),
                    }}>
                    <p>{t('S. No')}</p>
                  </th>
                )}
                {unifiedCols.map((item, index) => {
                  const isLeftSticky =
                    effectiveFrozenColumn !== null
                      ? frozenKeys.has(item.key)
                      : item.kind === 'column' && item.col.sticky === 'left';
                  const isRightSticky = item.kind === 'column' && item.col.sticky === 'right';
                  let stickyClass = '';
                  if (isLeftSticky) {
                    stickyClass = 'sticky-column sticky-left';
                  } else if (isRightSticky) {
                    stickyClass = 'sticky-column sticky-right';
                  }

                  if (item.kind === 'created') {
                    return (
                      <th
                        className={`table-header ${isLeftSticky ? 'sticky-column sticky-left' : ''}`}
                        key="createdAt"
                        style={{
                          minWidth: `${Math.max(columnWidths.createdAt || 150, 40)}px`,
                          width: `${columnWidths.createdAt || 150}px`,
                          flex: getCellFlex(columnWidths.createdAt || 150, isLeftSticky),
                          '--sticky-left': isLeftSticky
                            ? `${stickyOffsets.createdAt?.left}px`
                            : undefined,
                          '--sticky-right': isLeftSticky
                            ? `${stickyOffsets.createdAt?.left}px`
                            : undefined,
                        } as React.CSSProperties}>
                        <p>{t('created')}</p>
                      </th>
                    );
                  }
                  if (item.kind === 'updated') {
                    return (
                      <th
                        className={`table-header ${isLeftSticky ? 'sticky-column sticky-left' : ''}`}
                        key="updatedAt"
                        style={{
                          minWidth: `${Math.max(columnWidths.updatedAt || 150, 40)}px`,
                          width: `${columnWidths.updatedAt || 150}px`,
                          flex: getCellFlex(columnWidths.updatedAt || 150, isLeftSticky),
                          '--sticky-left': isLeftSticky
                            ? `${stickyOffsets.updatedAt?.left}px`
                            : undefined,
                          '--sticky-right': isLeftSticky
                            ? `${stickyOffsets.updatedAt?.left}px`
                            : undefined,
                        } as React.CSSProperties}>
                        <p>{t('lastUpdated')}</p>
                      </th>
                    );
                  }
                  const { col } = item;
                  const tooltipIndex = index;
                  const colKey = col.accessorKey.toString();
                  let stickyLeftValue: string | undefined;
                  let stickyRightValue: string | undefined;
                  if (isLeftSticky) {
                    stickyLeftValue = `${stickyOffsets[colKey]?.left}px`;
                    stickyRightValue = `${stickyOffsets[colKey]?.left}px`;
                  } else if (isRightSticky) {
                    stickyLeftValue = `${stickyOffsets[colKey]?.right}px`;
                    stickyRightValue = `${stickyOffsets[colKey]?.right}px`;
                  }

                  return (
                    <th
                      className={`table-header ${stickyClass}`}
                      key={`${colKey}-${col.label}`}
                      style={
                        {
                          minWidth: `${Math.max(columnWidths[colKey], 40)}px`,
                          width: `${columnWidths[colKey]}px`,
                          flex: getCellFlex(
                            columnWidths[colKey],
                            Boolean(isLeftSticky || isRightSticky),
                          ),
                          '--sticky-left': stickyLeftValue,
                          '--sticky-right': stickyRightValue,
                        } as React.CSSProperties
                      }>
                      <p>
                        {col.customLabel ? col.customLabel : col.label}
                        {col.headingDetails && col.headingDescription ? (
                          <span
                            className="tooltip-wrapper"
                            ref={(element) => {
                              wrapperReferences.current[tooltipIndex] = element;
                            }}
                            onMouseEnter={() => toggleTooltip(tooltipIndex)}
                            onMouseLeave={() =>
                              setTooltipState({ isOpen: false, style: {}, columnIndex: null })
                            }>
                            <InfoIcon className="question-icon" />

                            {tooltipState.isOpen &&
                              tooltipState.columnIndex === tooltipIndex &&
                              ReactDOM.createPortal(
                                <span
                                  className="heading-tooltip-description"
                                  style={tooltipState.style}>
                                  {col.headingDescription}
                                </span>,
                                document.body,
                              )}
                          </span>
                        ) : null}
                      </p>

                      {col.sortAllow ? (
                        <div
                          onClick={() => handleSortFieldTable(col.accessorKey as string)}
                          role="presentation">
                          {sortData?.sortField === col.accessorKey ? (
                            (sortData?.sortBy === 'asc' && (
                              <SortAsc className="sort-icon ascending" />
                            )) ||
                            (sortData?.sortBy === 'desc' && (
                              <SortDesc className="sort-icon descending" />
                            ))
                          ) : (
                            <SortDefault className="sort-icon default" />
                          )}
                        </div>
                      ) : null}
                      {col?.allowColumnResize !== false && (
                        <div
                          className="table-column-resizer"
                          onMouseDown={(event) =>
                            handleMouseDown(event, col.accessorKey.toString())
                          }
                          role="presentation"
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody
              className="table-body"
              style={{
                height: height ? `calc(100vh - ${height}px)` : `calc(100vh - 280px)`,
              }}>
              {loading && (
                <tr className="table-loading">
                  <td>
                    <BatteryLoader />
                  </td>
                </tr>
              )}
              {!loading && data.length === 0 && (
                <tr className="table-loading">
                  <td>
                    <div className="table-no-data-wrap">
                      {/* <img src={NoDataFound} alt="" className="no-data-found-image" /> */}
                      <NoDataSVG primaryColor="var(--primary-color)" />
                      <div className="no-data-found-details">
                        <h3>{t('oopsNothingHere')}</h3>
                        <p>{t('noDataMessage')} </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                data.length > 0 &&
                data.map((row, index) => (
                  <tr
                    role="presentation"
                    onClick={() => handleRowClickEvent(row)}
                    className={`table-row ${selectedRowId === row.id ? 'activeRow' : ''}`}
                    key={row.id}
                    style={
                      {
                        height: setHeightAutoToTr && 'auto',
                        '--header-length':
                          unifiedCols.length +
                          (serialNo ? 1 : 0) +
                          (checkbox ? 1 : 0) || 1,
                      } as React.CSSProperties
                    }>
                    {checkbox && (
                      <td
                        className={`table-row-data checkbox ${isSerialSticky ? 'sticky-column sticky-left' : ''}`}
                        style={
                          {
                            width: `${CHECKBOX_COLUMN_WIDTH}px`,
                            flex: isSerialSticky
                              ? getCellFlex(CHECKBOX_COLUMN_WIDTH, true)
                              : undefined,
                            '--sticky-left': isSerialSticky
                              ? `${stickyOffsets.__checkbox__?.left ?? 0}px`
                              : undefined,
                            '--sticky-right': isSerialSticky
                              ? `${stickyOffsets.__checkbox__?.left ?? 0}px`
                              : undefined,
                          } as React.CSSProperties
                        }>
                        <input
                          type="checkbox"
                          aria-label={t('selectRow')}
                          checked={selectedRows.includes(String(row[selectKey]))}
                          disabled={isRowSelectable ? !isRowSelectable(row) : false}
                          onChange={() => {
                            if (isRowSelectable && !isRowSelectable(row)) return;
                            handleCheckRow(row);
                          }}
                        />
                      </td>
                    )}

                    {serialNo && (
                      <td
                        className={`table-row-data ${isSerialSticky ? 'sticky-column sticky-left' : ''}`}
                        style={{
                          minWidth: `${Math.max(columnWidths.serialNo, 40)}px`,
                          width: `${columnWidths.serialNo}px`,
                          flex: getCellFlex(columnWidths.serialNo, isSerialSticky),
                          ...({
                            '--sticky-left': isSerialSticky
                              ? `${stickyOffsets.serialNo?.left ?? 0}px`
                              : undefined,
                            '--sticky-right': isSerialSticky
                              ? `${stickyOffsets.serialNo?.left ?? 0}px`
                              : undefined,
                          } as React.CSSProperties),
                        }}>
                        <p className="table-row-each-data">{(page - 1) * limit + index + 1}</p>
                      </td>
                    )}
                    {unifiedCols.map((item) => {
                      const isLeftSticky =
                        effectiveFrozenColumn !== null
                          ? frozenKeys.has(item.key)
                          : item.kind === 'column' && item.col.sticky === 'left';
                      const isRightSticky = item.kind === 'column' && item.col.sticky === 'right';
                      let stickyClass = '';
                      if (isLeftSticky) {
                        stickyClass = 'sticky-column sticky-left';
                      } else if (isRightSticky) {
                        stickyClass = 'sticky-column sticky-right';
                      }

                      if (item.kind === 'created') {
                        return (
                          <td
                            className={`table-row-data ${isLeftSticky ? 'sticky-column sticky-left' : ''}`}
                            key="createdAt"
                            style={{
                              padding: setHeightAutoToTr ? '5px 5px 5px 5px' : '',
                              minWidth: `${Math.max(columnWidths.createdAt || 150, 40)}px`,
                              width: `${columnWidths.createdAt || 150}px`,
                              flex: getCellFlex(columnWidths.createdAt || 150, isLeftSticky),
                              '--sticky-left': isLeftSticky
                                ? `${stickyOffsets.createdAt?.left}px`
                                : undefined,
                              '--sticky-right': isLeftSticky
                                ? `${stickyOffsets.createdAt?.left}px`
                                : undefined,
                            } as React.CSSProperties}>
                            <p className="table-row-each-data">
                              {ConvertUTCtoDateTime(
                                (row as any).createdAt ||
                                  (row as any).created ||
                                  (row as any).created_at ||
                                  '',
                              ) || '-'}
                            </p>
                          </td>
                        );
                      }
                      if (item.kind === 'updated') {
                        return (
                          <td
                            className={`table-row-data ${isLeftSticky ? 'sticky-column sticky-left' : ''}`}
                            key="updatedAt"
                            style={{
                              padding: setHeightAutoToTr ? '5px 5px 5px 5px' : '',
                              minWidth: `${Math.max(columnWidths.updatedAt || 150, 40)}px`,
                              width: `${columnWidths.updatedAt || 150}px`,
                              flex: getCellFlex(columnWidths.updatedAt || 150, isLeftSticky),
                              '--sticky-left': isLeftSticky
                                ? `${stickyOffsets.updatedAt?.left}px`
                                : undefined,
                              '--sticky-right': isLeftSticky
                                ? `${stickyOffsets.updatedAt?.left}px`
                                : undefined,
                            } as React.CSSProperties}>
                            <p className="table-row-each-data">
                              {ConvertUTCtoDateTime(
                                (row as any).updatedAt ||
                                  (row as any).lastUpdated ||
                                  (row as any).updated_at ||
                                  '',
                              ) || '-'}
                            </p>
                          </td>
                        );
                      }

                      const { col } = item;
                      const colKey = col.accessorKey.toString();
                      let stickyLeftValue: string | undefined;
                      let stickyRightValue: string | undefined;
                      if (isLeftSticky) {
                        stickyLeftValue = `${stickyOffsets[colKey]?.left}px`;
                        stickyRightValue = `${stickyOffsets[colKey]?.left}px`;
                      } else if (isRightSticky) {
                        stickyLeftValue = `${stickyOffsets[colKey]?.right}px`;
                        stickyRightValue = `${stickyOffsets[colKey]?.right}px`;
                      }

                      return (
                        <td
                          className={`table-row-data ${stickyClass}`}
                          key={`${colKey}-${col.label}`}
                          style={
                            {
                              padding: setHeightAutoToTr ? '5px 5px 5px 5px' : '',
                              minWidth: `${Math.max(columnWidths[colKey], 40)}px`,
                              width: `${columnWidths[colKey]}px`,
                              flex: getCellFlex(
                                columnWidths[colKey],
                                Boolean(isLeftSticky || isRightSticky),
                              ),
                              '--sticky-left': stickyLeftValue,
                              '--sticky-right': stickyRightValue,
                            } as React.CSSProperties
                          }>
                          {col.customData ? (
                            col.customData(row, index)
                          ) : (
                            <p
                              className={`table-row-each-data ${col?.queryParamsKey && col?.sidePanelComponent && !(col.isSidePanelDisabled && col.isSidePanelDisabled(row)) ? 'hyperlink' : ''}`}
                              role="presentation"
                              onClick={(event) => handleRowClick(row, col, event)}>
                              {HighlightText(
                                textToHighlight || null,
                                String(row[col.accessorKey] || ''),
                              )}
                            </p>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {widthOfContainers.right > 0 ? (
          <div
            className="adjustable-divider"
            role="presentation"
            onMouseDown={handleMouseDownFunction}
          />
        ) : (
          <div />
        )}
        {widthOfContainers.right > 0 ? (
          <div
            className="custom-table-side-panel"
            style={{
              width: `${widthOfContainers.right}%`,
              transition: `width ${widthOfContainers.applyTransitionEffect ? '0.2s' : '0.0s'} ease-in-out`,
            }}>
            <div className="side-panel-header">
              {dataKey === 'DeviceManagement' &&
                (!permissionCheck('device_advanced_settings') as any) ? (
                <span />
              ) : (
                <>
                  {sidePanelDetails.enableExpandFullScreen && explore && (
                    <div
                      className={`side-panel-header-left ${sidePanelDetails.enableExpandFullScreen ? 'active' : 'in-active'}`}>
                      <div className="button-wrap" style={{ cursor: 'pointer' }}>
                        <span role="presentation" onClick={() => handleSidePanelExpand(false)}>
                          <ExpandIcon />
                        </span>
                        <div className="hover-label">{t('expandPages', 'Expand Pages')}</div>
                      </div>
                      {!sidePanelDetails.disableNewTab && (
                        <div className="button-wrap" style={{ cursor: 'pointer' }}>
                          <span role="presentation" onClick={() => handleSidePanelExpand(true)}>
                            <NewTabIcon />
                          </span>
                          <div className="hover-label">
                            {t('expandNewPages', 'Expand New Pages')}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div
                    // style={{
                    //   height: height ? `calc(100vh - ${height}px)` : `calc(100vh - 280px)`,
                    // }}
                    className="side-panel-header-left">
                    {sidePanelTopLeftContent}
                  </div>
                </>
              )}
              <span role="presentation" onClick={handleCloseSidePanel}>
                <CloseIcon />
              </span>
            </div>
            <div
              className="side-panel-content"
              style={{
                height: height ? `calc(99vh - ${height}px)` : `calc(100vh - 280px)`,
              }}>
              {sidePanelEnabled && sidePanelManualContent ? sidePanelManualContent : sidePanelDetails.jsxElement}
            </div>
          </div>
        ) : (
          <div />
        )}
      </div>
      <div className="custom-table-footer">
        <div className="footer-left">{footerLeft}</div>
        <div className="footer-right">
          {!disablePagination && (
            <Pagination
              active={page}
              setActive={changePage}
              size={totalPages}
              limit={limit}
              totalResults={totalResults}
              loading={loading || false}
            />
          )}
        </div>
      </div>
    </div>
  );
}

CustomTable.defaultProps = {
  footerLeft: null,
  rightTopContent: null,
  leftTopContent: null,
  serialNo: false,
  stickySerialNo: false,
  created: false,
  lastUpdated: false,
  loading: false,
  actionButton: false,
  actionDropdown: false,
  sizeChangeButtonRequired: false,
  filterAllow: true,
  disablePagination: false,
  disableHeader: false,
  version: 'v2',
  activeSearchFilter: true,
  headingDetails: false,
  expandTableOpen: false,
  expandTableOpenKey: null,
  isRowSelectable: undefined,
  isExportRequired: true,
};

export default CustomTable;
